import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Moment from 'moment';
import clsx from 'clsx';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LogIcon from '@mui/icons-material/InsertDriveFile';
import DeliveryIcon from '@mui/icons-material/Send';
import SuccessIcon from '@mui/icons-material/Check';
import WarningIcon from '@mui/icons-material/PriorityHigh';
import ErrorIcon from '@mui/icons-material/Close';

import { get } from 'lodash';
import { format as d3Format } from 'd3';

import MultiGauge from '../../components/charts/multigauge';
import HealthPanel from '../../components/healthpanel';
import ConfirmationDlg from '../../components/confirmationdlg';

import SensorService from '../../data/sensors-service';
import HealthService, { IsVM, CoreTemp } from '../../data/health-service';
import Auth, { ProfileContext } from '../../data/auth-service';
import DataContext from '../../data/data-context';
import PolicyService from '../../data/policy-service';

import Helpers from '../../libraries/helpers';
import { toast } from 'react-toastify';

const useStyles = makeStyles(theme => ({
    fullWidth: {
        width: '100%',
    },
    panel: {
        marginTop: theme.spacing(1),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
    },
    hlist: {
        display: 'inline-block'
    },
    healthGrid: {
        flexGrow: 1,
    },
    key: {
        ...theme.typography.subtitle1,
        fontWeight: '600',
    },
    value: {
        ...theme.typography.subtitle2,
        color: theme.palette.info.light
    },
    divider: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    panelHeading: {
        ...theme.typography.button,
        paddingLeft: theme.spacing(1),
    },
    panelDetails: {
        paddingTop: 0,
        marginTop: -15,
    },
    smallButton: {
        padding: '0px',
        marginRight: theme.spacing(1),
    },
    success: {
        background: theme.palette.success.main,
        color: theme.palette.success.contrastText,
    },
    warning: {
        background: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
    },
    fail: {
        background: theme.palette.error.main,
        color: theme.palette.error.contrastText,
    },
    logFilter: {
        background: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        display: 'inline-flex',
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        borderRadius: '3px',
        lineHeight: '30px',
    },
    logBox: {
        background: theme.styled.paper.background,
        border: `1px solid ${theme.palette.primary.main}`,
        width: '100%',
        minHeight: '200px',
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    configBG: {
        backgroundColor: theme.palette.background.default,
    },
    errorBox: {
        backgroundColor: theme.styled.paper.background,
        border: `1px solid ${theme.palette.error.main}`,
    },
    warningBox: {

    },
    sensorLogs: {
        maxHeight: '300px',
        overflowY: 'scroll',
        overflowWrap: 'anywhere',
    },
    wrapDeliveries: {
        whiteSpace: 'break-spaces',
        overflowWrap: 'break-word',
    },
    shrinkMessages: {
        fontSize: '90%'
    },
    notificationPanel: {
        background: theme.styled.paper.background,
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    errorPanel: {
        border: `1px solid ${theme.palette.error.main}`,
    },
    warningPanel: {
        border: `1px solid ${theme.palette.warning.main}`,
    },
}));

const SensorHealth = () => {
    const classes = useStyles();
    const theme = useTheme();
    const params = useParams();
    const { updateProfile, getProfile, /*perm*/ } = useContext(ProfileContext);
    const { backtestingJobsLite } = useContext(DataContext);
    const [sensor, setSensor] = useState(null);
    const [configPolicy, setConfigPolicy] = useState({});
    const [logs, setLogs] = useState([]);
    const [logFilter, setLogFilter] = useState(null);
    const [filteredLogs, setFilteredLogs] = useState([]);
    const [history, setHistory] = useState({ history: [] });
    const [historyMetrics, setHistoryMetrics] = useState([]);
    const [scale, setScale] = useState('linear');
    const [expanded, setExpanded] = useState({});
    const [getLogsDisabled, setGetLogsDisabled] = useState(false);
    const [logsAnchor, setLogsAnchor] = useState(null);
    const [dialog, setDialog] = useState({
        open: false,
        message: '',
        title: '',
        cancelLabel: '',
        okLabel: '',
        onOK: () => {},
        onClose: () => {
            setDialog({
                ...dialog,
                open: false,
            });
        },
    });
    const [gauges, setGauges] = useState([]);
    const [friendlyName, setFriendlyName] = useState({});
    const logListRef = useRef(null);
    const sensorsPerms = Auth.permissions.get('Sensors');

    const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';
    const handleChangeScale = (e, v) => setScale(v);

    const handleExpandPanel = (key) => (event, isExpanded) => {
        setExpanded({ ...expanded, [key]: isExpanded });
    };

    const isAWSInstance = () => {
        const uuid = get(sensor, 'uuid');
        const instance = get(sensor, 'data.instance_id');
        return (uuid != null && uuid.length) && (instance != null && instance.length);
    };

    const isVM = () => {
        return IsVM(get(sensor, 'model'), get(sensor, 'health_metrics.system.machine_type'));
    };

    useEffect(() => {
        SensorService.getSensor(params.uuid, params.cmc).then(res => {
            setSensor(res);
            return res;
        }).then(res => {
            // the long and winding road that leads to the system_config policy
            PolicyService.getCurrentPolicy('system_config', res.host, params.cmc).then(res => {
                return res;
            }).then(res => {
                PolicyService.getPolicy('system_config', res).then(res => {
                    setConfigPolicy(res || {});
                });
            }).catch(err => { console.error(err) });
        });
        HealthService.getHealthHistory(params.uuid, params.cmc).then(res => {
            setHistory(res);
        }).catch(err => { console.error(err) });

        if (sensorsPerms.override) {
            SensorService.getSensorLogs(params.uuid).then(res => {
                if (res.length > 150) {
                    res.splice(150);
                }
                setLogs(res);
            }).catch(err => { console.error(err) });
        }

        const sensorHealthInterval = setInterval(() => {
            SensorService.getSensor(params.uuid, params.cmc).then(res => {
                setSensor(res);
                return res;
            });
        }, 11000);

        const healthHistortInterval = setInterval(() => {
            HealthService.getHealthHistory(params.uuid, params.cmc).then(res => {
                setHistory(res);
            }).catch(err => { console.error(err) });
        }, 60000);

        // read user pofile
        const profile = getProfile('sensor');
        setScale(profile.scale || 'linear');
        setExpanded(profile.details || {});

        return () => {
            clearInterval(sensorHealthInterval);
            clearInterval(healthHistortInterval);
        };
    }, [getProfile, params.cmc, params.uuid]);

    useEffect(() => {
        setHistoryMetrics(HealthService.getSensorHistoryMetrics(get(sensor, 'health_metrics.appliance_type', 'sensor') === 'btsensor'));
    }, [sensor]);

    useEffect(() => {
        setGauges([
            {
                title: 'CPU',
                val: () => get(sensor, 'health_metrics.incr_cpuinfo_pct.total.used') || 0
            },
            {
                title: 'RAM',
                val: () => {
                    const avail = get(sensor, 'health_metrics.system.meminfo.avail_memory') || 0;
                    const total = get(sensor, 'health_metrics.system.meminfo.total_memory') || 1;
                    return 100 * (1.0 - avail / total);
                }
            },
            {
                title: 'Disk',
                val: () => {
                    const disk = (get(sensor, 'health_metrics.disks') || [])
                        .find(d => d.mount_point === '/') || { utilization: 0 };
                    return 100 * disk.utilization;
                },
                severities: { green: 82, red: 101 }
            },
            {
                title: 'Inodes',
                val: () => {
                    const disk = (get(sensor, 'health_metrics.disks') || [])
                        .find(d => d.mount_point === '/') || { inode_utilization: 0 };
                    return 100 * disk.inode_utilization;
                },
            },

            {
                title: 'Pcap Disk',
                val: () => {
                    return 100 * get(sensor, 'health_metrics.captured.full_pcap.disk_usage', 0);
                },
                severities: { green: get(configPolicy, 'data.retention.full_pcap_disk_usage', 50), red: 101 }
            },
            {
                title: 'SmartPcap Disk',
                val: () => {
                    return 100 * get(sensor, 'health_metrics.eventd.smart_pcap.disk_usage', 0);
                },
                severities: { green: get(configPolicy, 'data.retention.smart_pcap.disk_usage', 12), red: 101 }
            },
            {
                title: 'Malware Disk',
                val: () => {
                    return 100 * get(sensor, 'health_metrics.mwd.storedir.disk_usage', 0);
                },
                severities: { green: 100 * get(sensor, 'health_metrics.mwd.storedir.disk_usage_limit', 0.3), red: 101 }
            },
        ]);
    }, [sensor, configPolicy]);

    const handleOpenLogsMenu = (e) => {
        setLogsAnchor(e.currentTarget);
    };

    const handleCloseLogsMenu = () => {
        setLogsAnchor(null);
    };

    const handleGetLogs = rules => {
        toast.info('Please wait while the logs are gathered. This may take awhile...');
        setGetLogsDisabled(true);
        setLogsAnchor(null);

        const t = Auth.getAuth().token;
        let url = `/api/sensors/${sensor.host}/logdump/?t=${t}${rules ? '&rules=true' : ''}`;
        if (MC2CHECK) {
            url = `/api/mc2/sensors/${params.cmc}/${sensor.host}/health/logdump/${rules ? '&rules=true' : ''}`;
        }

        fetch(url, {
            method: 'GET',
            headers: { Authorization: `Bearer ${t}` },
        }).then(response => {
            if (response.status === 200) {
                const contentDisposition = response.headers.get('content-disposition') || '';
                const filename = (contentDisposition.split('filename')[1])
                    ?.split('=')[1] // extract filename
                    .replace(/^"(.*)"$/, '$1') // remove quotes
                    .trim();
                response.arrayBuffer().then(buffer => {
                    const blob = new Blob([buffer], { type: 'application/x-gzip' });
                    if (window.navigator.msSaveOrOpenBlob) {
                        window.navigator.msSaveOrOpenBlob(blob, filename);
                    } else {
                        const a = document.createElement('a');
                        a.style = 'display: none';
                        document.body.appendChild(a);
                        a.href = window.URL.createObjectURL(blob);
                        a.download = filename;
                        a.click();
                        a.remove();
                        window.URL.revokeObjectURL(blob);
                    }
                }, err => {
                    console.error(err);
                });
            } else {
                setGetLogsDisabled(false);
                toast.error('Unable to retrieve sensor logs...');
            }
            setGetLogsDisabled(false);
        });
    };

    useEffect(() => {
        if (!sensor) return;

        const btjob = Object.values(backtestingJobsLite || {}).find(b => b.sensor === sensor.uuid);
        setFriendlyName({ btconf: btjob ? btjob.name : undefined });
    }, [backtestingJobsLite, sensor]);

    // stash profile hook
    useEffect(() => {
        updateProfile('sensor', { details: expanded, scale });
    }, [scale, expanded, updateProfile]);

    const handleFilterLogs = (data) => {
        setLogFilter(data);
    };

    useEffect(() => {
        if (logFilter) {
            const lfDate = new Date(logFilter.timestamp);
            setFilteredLogs(logs.filter(v => (new Date(v.timestamp) <= lfDate && logFilter.log.filename === v.log.filename)));
            if (logListRef && logListRef.current) {
                logListRef.current.scrollTop = 0;
            }
        } else {
            setFilteredLogs([]);
        }
    }, [logs, logFilter]);

    return (
        <React.Fragment>
            {(get(sensor, 'health_metrics.issues', []) || []).map((issue, i) =>
                <Box key={`${issue.id}_${i}`}
                    className={clsx({
                        [classes.notificationPanel]: sensor && sensor.health_status,
                        [classes.errorPanel]: sensor && sensor.status === 'critical',
                        [classes.warningPanel]: sensor && sensor.status === 'warning',
                    })}>
                    {issue.message}
                </Box>
            )}
            <Box display="flex" justifyContent="flex-end">
                {sensorsPerms.override && <Box><Button
                    variant="contained"
                    aria-label="get logs"
                    color="primary"
                    onClick={handleOpenLogsMenu}
                    disabled={getLogsDisabled || !sensorsPerms.override}
                >
                    Get Logs
                </Button></Box>}
                <Menu open={Boolean(logsAnchor)} anchorEl={logsAnchor} onClose={handleCloseLogsMenu}>
                    <MenuItem onClick={() => { handleGetLogs(false) }}>Just Logs</MenuItem>
                    <MenuItem onClick={() => { handleGetLogs(true) }}>Logs and Suricata Rules</MenuItem>
                </Menu>
            </Box>
            <Box className={classes.panel} mb={7}>
                <Grid container className={classes.healthGrid} spacing={2}>
                    <Grid container item xs={12} spacing={1}>
                        <Grid item xs={12}>
                            <Box><Typography variant="h6">{get(sensor, 'data.name')}</Typography></Box>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography className={classes.key}>Host</Typography>
                            <Typography className={classes.value}>
                                {get(sensor, 'host')}
                            </Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography className={classes.key}>Version</Typography>
                            <Typography className={classes.value}>
                                {get(sensor, 'health_metrics.version')}
                            </Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography className={classes.key}>Model</Typography>
                            <Typography className={classes.value}>
                                {get(sensor, 'model')}
                            </Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography className={classes.key}>Machine Type</Typography>
                            <Typography className={classes.value}>
                                {get(sensor, 'health_metrics.system.machine_type')}
                            </Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography className={classes.key}>Mode</Typography>
                            <Typography className={classes.value}>
                                {get(sensor, 'health_metrics.sensor_mode')}
                            </Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography className={classes.key}>Last Alert Received</Typography>
                            <Typography className={classes.value}>
                                {get(sensor, 'last_alert')
                                    ? Moment(get(sensor, 'last_alert')).format(theme.date.longtz)
                                    : '--'}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12} spacing={1}>
                        <Grid item xs={2}>
                            <Typography className={classes.key}>Cores</Typography>
                            <Typography className={classes.value}>
                                {get(sensor, 'health_metrics.system.cpuinfo.total.num_cores')}
                            </Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography className={classes.key}>RAM</Typography>
                            <Typography className={classes.value}>
                                {Helpers.formatBytes(get(sensor, 'health_metrics.system.meminfo.total_memory') * 1000)}
                            </Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography className={classes.key}>Running / Total Procs</Typography>
                            <Typography className={classes.value}>
                                {get(sensor, 'health_metrics.system.load_avg.running_procs')} / {get(sensor, 'health_metrics.system.load_avg.total_procs')}
                            </Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography className={classes.key}>MAC Address</Typography>
                            <Typography className={classes.value}>
                                {get(sensor, 'health_metrics.system.mac_mgmnt') || 'Unknown'}
                            </Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography className={classes.key}>UUID</Typography>
                            <Typography className={classes.value}>
                                {get(sensor, 'uuid')}
                            </Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography className={classes.key}>Boot Time</Typography>
                            <Typography className={classes.value}>
                                {Moment(get(sensor, 'health_metrics.system.boot_time')).format(theme.date.longtz)}
                            </Typography>
                        </Grid>
                    </Grid>

                    {isAWSInstance() && <Grid container item xs={6} spacing={1} alignContent="flex-start">
                        <Grid item xs={12}>
                            <Box><Typography>AWS</Typography><Divider /></Box>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography className={classes.key}>AWS Instance ID</Typography>
                            <Typography className={classes.value}>
                                {get(sensor, 'data.instance_id')}
                            </Typography>
                        </Grid>
                        {get(sensor, 'data.asg_name') &&
                            <Grid item xs={3}>
                                <Typography className={classes.key}>AWS Group Name</Typography>
                                <Typography className={classes.value}>
                                    {get(sensor, 'data.asg_name')}
                                </Typography>
                            </Grid>
                        }
                        {get(sensor, 'data.avail_zone') &&
                            <Grid item xs={3}>
                                <Typography className={classes.key}>AWS Avail. Zone</Typography>
                                <Typography className={classes.value}>
                                    {get(sensor, 'data.avail_zone')}
                                </Typography>
                            </Grid>
                        }
                        {get(sensor, 'data.instance_state') &&
                            <Grid item xs={3}>
                                <Typography className={classes.key}>AWS Instance State</Typography>
                                <Typography className={classes.value}>
                                    {get(sensor, 'data.instance_state')}
                                </Typography>
                            </Grid>
                        }
                    </Grid>}
                    {!isVM() &&
                        <Grid container item xs={6} spacing={1} alignContent="flex-start">
                            <Grid item xs={12}><Typography>Hardware</Typography><Divider /></Grid>
                            <Grid item xs={4}>
                                <Typography className={classes.key}>Serial Number</Typography>
                                <Typography className={classes.value}>
                                    {get(sensor, 'health_metrics.system.serialno', 0) || 'Unknown'}
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography className={classes.key}>Temp Crit (°C)</Typography>
                                <Typography className={classes.value}>
                                    {CoreTemp(get(sensor, 'health_metrics.coretemp.temp_crit', 0))}
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography className={classes.key}>Temp Max (°C)</Typography>
                                <Typography className={classes.value}>
                                    {CoreTemp(get(sensor, 'health_metrics.coretemp.temp_max', 0))}
                                </Typography>
                            </Grid>
                        </Grid>
                    }
                    {/* gre */}
                    {get(sensor, 'data.gre_remote') && <Grid container item xs={6} spacing={1} alignContent="flex-start">
                        <Grid item xs={12}>
                            <Box width="100%"><Typography>GRE</Typography><Divider /></Box>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography className={classes.key}>Remote Host</Typography>
                            <Typography className={classes.value}>
                                {get(sensor, 'data.gre_remote')}
                            </Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography className={classes.key}>Local Host</Typography>
                            <Typography className={classes.value}>
                                {get(sensor, 'data.gre_local')}
                            </Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography className={classes.key}>Key</Typography>
                            <Typography className={classes.value}>
                                {get(sensor, 'data.gre_key')}
                            </Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography className={classes.key}>MTU</Typography>
                            <Typography className={classes.value}>
                                {get(sensor, 'data.gre_mtu')}
                            </Typography>
                        </Grid>
                    </Grid>}
                    {/* full pcap metrics */}
                    <Grid container item xs={6} spacing={2} alignContent="flex-start">
                        <Grid item xs={12}>
                            <Box width="100%"><Typography>PCAP</Typography><Divider /></Box>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography className={classes.key}>Full Files</Typography>
                            <Typography className={classes.value}>
                                {d3Format(',')(get(sensor, 'health_metrics.captured.full_pcap.nr_files', 0))}
                            </Typography>
                        </Grid>
                        {get(sensor, 'health_metrics.captured.full_pcap.oldest_idx') &&
                            <Grid item xs={4}>
                                <Typography className={classes.key}>Oldest Full</Typography>
                                <Typography className={classes.value}>
                                    {Moment(get(sensor, 'health_metrics.captured.full_pcap.oldest_idx')).format(theme.date.longtz)}
                                </Typography>
                            </Grid>
                        }
                        {get(sensor, 'health_metrics.captured.full_pcap.latest_idx') &&
                            <Grid item xs={4}>
                                <Typography className={classes.key}>Newest Full</Typography>
                                <Typography className={classes.value}>
                                    {Moment(get(sensor, 'health_metrics.captured.full_pcap.latest_idx')).format(theme.date.longtz)}
                                </Typography>
                            </Grid>
                        }
                        {/* smart pcap metrics */}
                        <Grid item xs={4}>
                            <Typography className={classes.key}>Smart Files</Typography>
                            <Typography className={classes.value}>
                                {d3Format(',')(get(sensor, 'health_metrics.eventd.smart_pcap.nr_files', 0))}
                            </Typography>
                        </Grid>
                        {get(sensor, 'health_metrics.eventd.smart_pcap.oldest') &&
                            <Grid item xs={4}>
                                <Typography className={classes.key}>Oldest Smart</Typography>
                                <Typography className={classes.value}>
                                    {Moment(get(sensor, 'health_metrics.eventd.smart_pcap.oldest')).format(theme.date.longtz)}
                                </Typography>
                            </Grid>
                        }
                        {get(sensor, 'health_metrics.eventd.smart_pcap.latest') &&
                            <Grid item xs={4}>
                                <Typography className={classes.key}>Newest Smart</Typography>
                                <Typography className={classes.value}>
                                    {Moment(get(sensor, 'health_metrics.eventd.smart_pcap.latest')).format(theme.date.longtz)}
                                </Typography>
                            </Grid>
                        }
                    </Grid>
                    {/* malware metrics */}
                    {Boolean((get(sensor, 'health_metrics.mwd.storedir.nr_files', 0))) && <Grid container item xs={6} spacing={2} alignContent="flex-start">
                        <Grid item xs={12}>
                            <Box width="100%"><Typography>Malware</Typography><Divider /></Box>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography className={classes.key}>Malware Sample Files</Typography>
                            <Typography className={classes.value}>
                                {d3Format(',')(get(sensor, 'health_metrics.mwd.storedir.nr_files', 0))}
                            </Typography>
                        </Grid>
                        {get(sensor, 'health_metrics.mwd.storedir.oldest') &&
                            <Grid item xs={4}>
                                <Typography className={classes.key}>Oldest Malware Sample</Typography>
                                <Typography className={classes.value}>
                                    {Moment(get(sensor, 'health_metrics.mwd.storedir.oldest')).format(theme.date.longtz)}
                                </Typography>
                            </Grid>
                        }
                        {get(sensor, 'health_metrics.mwd.storedir.latest') &&
                            <Grid item xs={4}>
                                <Typography className={classes.key}>Newest Malware Sample</Typography>
                                <Typography className={classes.value}>
                                    {Moment(get(sensor, 'health_metrics.mwd.storedir.latest')).format(theme.date.longtz)}
                                </Typography>
                            </Grid>
                        }
                    </Grid>}
                </Grid>

                <Divider className={classes.divider}/>

                <Grid container justifyContent="space-evenly" style={{ marginBottom: theme.spacing(3) }}>
                    {gauges.map((v, i) => {
                        return (<MultiGauge
                            label={v.title}
                            value={v.val()}
                            key={`${v.title}-${i}`}
                            data={v.data}
                            severities={v.severities}
                            small={v.small}
                            size={v.size}
                            colors={v.colors}/>);
                    })}
                </Grid>
                <Grid container spacing={2} direction="column" alignItems="flex-end">
                    <Grid item>
                        <ToggleButtonGroup size="small" value={scale} exclusive onChange={handleChangeScale}>
                            <ToggleButton aria-label="logarithmic scale" value="log">
                                Logarithmic Scale
                            </ToggleButton>
                            <ToggleButton aria-label="linear scale" value="linear">
                                Linear Scale
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>
                </Grid>
                {sensorsPerms.override && <Box display={MC2CHECK ? 'none' : 'block'}>
                    <Accordion expanded={!!expanded.logs} onChange={handleExpandPanel('logs')}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <LogIcon />
                            <Typography className={classes.panelHeading}>Sensor Logs</Typography>
                        </AccordionSummary>
                        <AccordionDetails className={classes.panelDetails}>
                            <Box width="100%">
                                {logFilter && <Box className={classes.logFilter}>
                                    <Box>Filter</Box>
                                    <Box ml={2}>-</Box>
                                    <Box ml={2}>Filename: {logFilter && logFilter.log.filename}</Box>
                                    <Box ml={2}>Before: {logFilter && Moment(logFilter.timestamp).format(theme.date.long)}</Box>
                                    <Box ml={2}><IconButton size="small" onClick={() => { handleFilterLogs(null) }}><ErrorIcon /></IconButton></Box>
                                </Box>}
                                <Box className={classes.logBox}>
                                    <List className={classes.sensorLogs} ref={logListRef}>
                                        {(!filteredLogs.length && !logs.length) && <ListItem>
                                            <Box width="100%">
                                                <Box>No logs available.</Box>
                                            </Box>
                                        </ListItem>}
                                        {(filteredLogs.length ? filteredLogs : logs).map((v, i) => {
                                            return (
                                                <ListItem key={'sensor-log-' + i} button onClick={() => { handleFilterLogs(v) }}>
                                                    <Box width="100%">
                                                        <Box>{v.log.filename}</Box>
                                                        <Box>{v.log.text}</Box>
                                                    </Box>
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                </Box>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Box>}
                <Accordion expanded={!!expanded.deliveries} onChange={handleExpandPanel('deliveries')}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <DeliveryIcon />
                        <Typography className={classes.panelHeading}>Latest Deliveries</Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.panelDetails}>
                        <List className={classes.fullWidth}>
                            <ListItem>
                                <Grid container spacing={2}>
                                    <Grid item xs={2}>Delivered to sensor</Grid>
                                    <Grid item xs={2}>Group</Grid>
                                    <Grid item xs={2}>Finish Time</Grid>
                                    <Grid item xs={1}>Duration (s)</Grid>
                                    <Grid item xs={4}>File</Grid>
                                    <Grid item xs={1}>Message</Grid>
                                </Grid>
                            </ListItem>
                            {sensor && sensor.deliveries && Object.keys(sensor.deliveries).map((v, i) => {
                                if (v === 'api' || v === 'eventd_filters') return null;
                                return (
                                    <ListItem key={'sensor-delivery-' + i}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={2}>
                                                <IconButton
                                                    className={clsx({
                                                        [classes.smallButton]: true,
                                                        [classes.success]: sensor.deliveries[v].success,
                                                        [classes.warning]: sensor.deliveries[v].warning,
                                                        [classes.fail]: !sensor.deliveries[v].success && !sensor.deliveries[v].warning
                                                    })}
                                                    aria-label={sensor.deliveries[v].success ? 'success' : sensor.deliveries[v].warning ? 'warning' : 'error'}
                                                    size="large">
                                                    {sensor.deliveries[v].success ? <SuccessIcon /> : sensor.deliveries[v].warning ? <WarningIcon /> : <ErrorIcon />}
                                                </IconButton>
                                                {v}
                                            </Grid>
                                            <Grid item xs={2}>{friendlyName[sensor.deliveries[v].type] || sensor.deliveries[v].name }</Grid>
                                            <Grid item xs={2}>{Moment(sensor.deliveries[v].finish_time).format(theme.date.long)}</Grid>
                                            <Grid item xs={1}>{Moment(sensor.deliveries[v].finish_time).subtract(sensor.deliveries[v].start_time).format('ss')}</Grid>
                                            <Grid item xs={4} className={classes.wrapDeliveries}>{sensor.deliveries[v].file}</Grid>
                                            <Grid item xs={1} className={classes.shrinkMessages}>{sensor.deliveries[v].message}</Grid>
                                        </Grid>
                                    </ListItem>
                                );
                            })}
                        </List>
                    </AccordionDetails>
                </Accordion>
                {historyMetrics.map(h =>
                    (<HealthPanel
                        key={h.name}
                        info={h}
                        data={history}
                        scale={scale}
                        uuid={params.uuid}
                        type={'sensor'}
                        expanded={!!expanded[h.name]}
                        onExpand={handleExpandPanel(h.name)}/>)
                )}
            </Box>
            <ConfirmationDlg {...dialog} />
        </React.Fragment>
    );
};

export default SensorHealth;
